/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: =, Brian Bal, Greg Sarpy, Ivan Ryabov, Leonid Khramov, bohdev, Slobodan Djordjevic
 */
import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import moment from "moment"
import { Button, Icon, DropdownMenu, trackEvent, Element, Label } from "trux"
import Draw from "../../util/draw"
import { PanelTitle } from "../../components/PanelTitle"
import { TopStatCard } from "../../components/TopStatCard.js"
import FeedbackOverlay from "../../components/FeedbackOverlay"
import { YourTracerMissingContent } from "../../components/MissingSensors"
import * as links from "../../util/links"
import * as playlistStore from "../../util/playlistStore"
import { formatFirstName, formatLastName } from "../../trace/formatters"
import {
    calculateBaseballStats as _calculateBaseballStats,
    facebookShare,
    nativeShare,
    twitterShare,
} from "../../util/helpers"
import "./PlayerDetailsPanelPage.css"
import { PlayerDetailsPanelRowBaseball } from "../PlayerDetailsPanelRowBaseball"

function PlayerDetailsPanelComp(props) {
    function filterHalves(selectedHalf) {
        props.selectHalf(selectedHalf)
    }

    function calculateBaseballStats(inning = "all-innings") {
        let stats = []
        if (props.game) {
            props.setBaseballStats({
                loading: true,
                complete: false,
                error: null,
            })
            stats = _calculateBaseballStats(props.game, inning)
        }
        props.setBaseballStats({
            value: stats,
            loading: false,
            complete: true,
            error: null,
        })
    }

    function closePressed() {
        let noPanelUrl = links.gamePlayersUrl(
            props.teamName,
            props.playlistId,
            props.gameId,
            props.eventNumber
        )
        props.history.push(noPanelUrl)
    }

    useEffect(() => {
        drawHeatmap()
        props.load(
            props.match.params.team_id,
            props.match.params.game_id,
            props.match.params.player_id
        )
        trackEvent({
            action: "Player Details Panel Opened",
            element: document.querySelector(".PlayerDetailsPanel"),
        })
    }, [])

    useEffect(() => {
        drawHeatmap()
    })

    function renderLoading() {
        let noPanelUrl = links.gameUrl(
            props.teamName,
            props.playlistId,
            props.gameId,
            props.eventNumber
        )

        return (
            <Element
                data-trk-ctx="Player Details Panel"
                className="PlayerDetailsPanel"
            >
                <Element
                    className="side-panel-backdrop"
                    onClick={(e) => props.history.push(noPanelUrl)}
                />
                <Element className="side-panel-buddy" />
                <Element className="players-panel side-panel extended-panel-title">
                    <Element className="side-panel-content">
                        <Label className="loading">
                            <i className="trace-icon loading-ball-gray" />
                            <span>Loading &hellip;</span>
                        </Label>
                    </Element>
                </Element>
            </Element>
        )
    }

    function renderPanel() {
        let dismissUrl = links.gameUrl(
            props.teamName,
            props.playlistId,
            props.gameId,
            props.eventNumber
        )

        let rosterAddUrl = links.rosterAddPlayerUrl(
            props.teamName,
            props.match.params.playlist_id,
            props.match.params.game_id,
            props.match.params.event_number,
            props.match.params.trace_number,
            props.player.team_player_id
        )
        let rosterSwapUrl = links.rosterSwapPlayerUrl(
            props.teamName,
            props.match.params.playlist_id,
            props.match.params.game_id,
            props.match.params.event_number,
            props.match.params.trace_number,
            props.player.team_player_id
        )
        let rosterEditUrl = links.rosterEditPlayerUrl(
            props.teamName,
            props.match.params.playlist_id,
            props.match.params.game_id,
            props.match.params.event_number,
            props.match.params.trace_number,
            props.player.team_player_id
        )

        const onChange = (e, val) => {
            if (val === "add") {
                props.history.push(rosterAddUrl)
            } else if (val === "edit") {
                props.history.push(rosterEditUrl)
            } else if (val === "swap") {
                props.history.push(rosterSwapUrl)
            } else if (val === "remove") {
                props.history.push(rosterSwapUrl + "/-1")
            }
        }

        let firstName = formatFirstName(props.player)
        let lastName = formatLastName(props.player)
        let panelTitle =
            props.player.jersey_number === ""
                ? `${firstName} ${lastName}`
                : `#${props.player.jersey_number} ${firstName} ${lastName}`
        let userId = props.player?.user.user_id
        var isDummy = /Tracer/gi.test(firstName)

        let options
        if (isDummy) {
            options = [
                {
                    title:
                        "Add a player to Tracer #" + props.player.trace_number,
                    value: "add",
                },
            ]
        } else {
            options = [
                {
                    title: "Remove from Tracer #" + props.player.trace_number,
                    value: "remove",
                },
                {
                    title: "Move to another Tracer",
                    value: "swap",
                },
                {
                    title: "Contact Info + jersey number",
                    value: "edit",
                },
            ]
        }

        const shareOptions = [
            {
                title: "Facebook",
                value: "facebook",
                iconType: Icon.Type.Facebook,
            },
            {
                title: "Twitter",
                value: "twitter",
                iconType: Icon.Type.Twitter,
            },
            {
                title: "Copy link",
                value: "copyUrl",
                iconType: Icon.Type.Link,
            },
            {
                title: "Email",
                value: "email",
                iconType: Icon.Type.Email,
            },
        ]

        const onShareClicked = (e) => {
            let text = `Watch this moment from Trace #TraceMoments`

            if (
                nativeShare(shareLink, text, "trace", "share", "native-share")
            ) {
                trackEvent({
                    action: "Native Share Pressed",
                    label: "Share Button",
                    element: e.target,
                })
            }
        }

        const onShare = (e, val) => {
            switch (val) {
                case "facebook":
                    facebookShare(shareLink, userId, null, e.target)
                    break
                case "twitter":
                    twitterShare(shareLink, userId, null, e.target)
                    break
                case "copyUrl":
                    copy(e, props.toast)
                    trackEvent({
                        action: "Copy Link Pressed",
                        label: "Share Button",
                        element: e.target,
                    })
                    break
                case "email":
                    if (e) e.preventDefault()
                    let dateFormatted = moment(props.game?.date).format(
                        "MMMM Do, YYYY"
                    )
                    let link = playerEmailLink(
                        emailShareLink,
                        props.player?.user,
                        dateFormatted
                    )
                    Object.assign(document.createElement("a"), {
                        target: "_blank",
                        href: link,
                    }).click()
                    trackEvent({
                        action: "Email Pressed",
                        label: "Share Button",
                        element: e.target,
                    })
                    break
                default:
                    break
            }
        }

        let shareLink = window.location.href.replace("/#", "")
        shareLink += "?utm_campaign=player"
        shareLink += "&utm_source=share"
        shareLink += "&utm_medium=copy"

        let emailShareLink = window.location.href.replace("/#", "")
        emailShareLink += "?utm_source=player"
        emailShareLink += "&utm_campaign=share"
        emailShareLink += "&utm_medium=email"

        let isTraceFamilyGame =
            props.game && props.game.division?.type === "family" ? true : false

        return (
            <Element
                data-trk-ctx="Player Detail Panel"
                className="PlayerDetailsPanel"
            >
                <Element
                    className="side-panel-backdrop"
                    onClick={(e) => props.history.push(dismissUrl)}
                />
                <Element className="side-panel-buddy" />

                <Element className="player-panel side-panel">
                    <PanelTitle
                        onClosePressed={closePressed}
                        filterHalves={filterHalves}
                        hideFilters={props.isPanoramicSensorless}
                        title={panelTitle}
                        userId={userId}
                        filterInnings={(inning) =>
                            calculateBaseballStats(inning)
                        }
                    />
                    <Element className="side-panel-content">
                        <section className="buttons mt-8 mb-8">
                            {props.playerPlaylist &&
                                props.playerPlaylist.event_count -
                                    props.goalCount >
                                    0 && (
                                    <Button
                                        trkAction="Watch Pressed"
                                        href={links.gameNotesUrl(
                                            props.teamName,
                                            props.playerPlaylist.id,
                                            props.gameId,
                                            props.playlistEventId
                                        )}
                                        content={
                                            <>
                                                Watch
                                                <br />
                                                Moments
                                            </>
                                        }
                                        icon={Icon.Type.Moment}
                                        margin="0 2"
                                    />
                                )}
                            <DropdownMenu
                                title="Share"
                                options={shareOptions}
                                onChange={onShare}
                                buttonWidth={"56"}
                                buttonHeight={"50"}
                                menuDirection={"bottom"}
                                menuPortal={"#modal-root"}
                                offsetAxisX={14}
                                offsetAxisY={0}
                                button={
                                    <Button
                                        trkAction="Share Button Pressed"
                                        icon={Icon.Type.Action}
                                        content="Share"
                                        margin="0 2"
                                        onClick={(e) => onShareClicked(e)}
                                    />
                                }
                            />
                            {isDummy && (
                                <Button
                                    trkAction="Add Player Pressed"
                                    href={rosterAddUrl}
                                    icon={Icon.Type.Add}
                                    content="Add Player"
                                    margin="0 2"
                                />
                            )}
                            {!isDummy && props.canEdit && (
                                <DropdownMenu
                                    title="More"
                                    options={options}
                                    onChange={onChange}
                                    buttonWidth={"auto"}
                                    menuDirection={"bottom"}
                                    menuPortal={"#modal-root"}
                                    button={
                                        <Button
                                            trkAction="More Pressed"
                                            icon={Icon.Type.More}
                                            content="More"
                                            margin="0 2"
                                        />
                                    }
                                />
                            )}
                        </section>

                        {props.missingStats === true && !props.isPanoramicSensorless && (
                            <>
                                <YourTracerMissingContent
                                    tn={props.traceNumber}
                                />
                            </>
                        )}

                        {props.missingStats === false &&
                            props.sportType === "baseball" && (
                                <PlayerDetailsPanelRowBaseball
                                    stats={props.stats}
                                    playerCount={
                                        props.playerStatsBaseball?.length
                                    }
                                    isTraceFamilyGame={isTraceFamilyGame}
                                />
                            )}

                        {props.missingStats === false &&
                            props.sportType === "soccer" && (
                                <>
                                    <section className="side-panel-section">
                                        <h4 className="performance">
                                            Performance
                                        </h4>
                                        <Element className="TopStats">
                                            <TopStatCard
                                                statValue={
                                                    props.stats.effort
                                                        ? props.stats.effort.toFixed(
                                                              0
                                                          )
                                                        : 0
                                                }
                                                statRank={
                                                    props.stats.effort_rank
                                                }
                                                playerCount={
                                                    props.playerStats.length
                                                }
                                                statTitle="# Max Efforts"
                                                isTraceFamilyGame={
                                                    isTraceFamilyGame
                                                }
                                            />
                                            <TopStatCard
                                                statValue={props.stats.stamina.toFixed(
                                                    0
                                                )}
                                                statTitle="Stamina"
                                                statRank={
                                                    props.stats.stamina_rank
                                                }
                                                playerCount={
                                                    props.playerStats.length
                                                }
                                                isTraceFamilyGame={
                                                    isTraceFamilyGame
                                                }
                                            />
                                            <TopStatCard
                                                statValue={props.stats.distance.toFixed(
                                                    1
                                                )}
                                                statTitle="Distance (Miles)"
                                                statRank={
                                                    props.stats.distance_rank
                                                }
                                                playerCount={
                                                    props.playerStats.length
                                                }
                                                isTraceFamilyGame={
                                                    isTraceFamilyGame
                                                }
                                            />
                                            <TopStatCard
                                                statValue={props.stats.top_speed.toFixed(
                                                    1
                                                )}
                                                statTitle="Top Speed (mph)"
                                                statRank={
                                                    props.stats.top_speed_rank
                                                }
                                                playerCount={
                                                    props.playerStats.length
                                                }
                                                isTraceFamilyGame={
                                                    isTraceFamilyGame
                                                }
                                            />
                                            {props.showStats && (
                                                <TopStatCard
                                                    statValue={
                                                        props.stats
                                                            .minutes_played
                                                            ? props.stats.minutes_played.toFixed(
                                                                  0
                                                              )
                                                            : "--"
                                                    }
                                                    statTitle="Minutes Played"
                                                    statRank={
                                                        props.stats
                                                            .minutes_played_rank
                                                    }
                                                    playerCount={
                                                        props.playerStats.length
                                                    }
                                                    isTraceFamilyGame={
                                                        isTraceFamilyGame
                                                    }
                                                />
                                            )}
                                        </Element>
                                    </section>

                                    <section className="side-panel-section">
                                        <h4>Heatmap</h4>
                                        <canvas
                                            id="player-heatmap"
                                            className="panel-field-portrait"
                                        />
                                    </section>
                                </>
                            )}
                    </Element>
                    <textarea
                        readOnly
                        id="copy-link"
                        style={{
                            height: "1px",
                            width: "1px",
                            position: "absolute",
                            top: "-999px",
                            left: "-999px",
                        }}
                        value={shareLink}
                    />
                </Element>
            </Element>
        )
    }

    function playerEmailLink(url, player, gameDate) {
        var subject = `${player.first_name} ${player.last_name}'s Video and Stats`

        var body = `Check out ${player.first_name} ${player.last_name}'s Video and Stats from ${gameDate}\n`
        body += "\n"
        body += url
        body += "\n"
        body += "\n"
        body += "Created by TRACE\n"
        body += "Soccer Artificial Intelligence\n"
        body += "\n"
        body += "\n"
        var encBody = encodeURIComponent(body)

        return `mailto:?BCC=sharingiscaring@traceup.com&Subject=${subject}&Body=${encBody}`
    }

    function copy(e, toast) {
        e.preventDefault()
        var el = document.getElementById("copy-link")
        el.contentEditable = true
        el.readOnly = true

        var range = document.createRange()
        range.selectNodeContents(el)
        var sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range) // Does not work for Firefox if a textarea or input
        if (el.nodeName === "TEXTAREA" || el.nodeName === "INPUT") {
            el.select() // Firefox will only select a form element with select()
        }
        if (
            el.setSelectionRange &&
            navigator.userAgent.match(/ipad|ipod|iphone/i)
        ) {
            el.setSelectionRange(0, 999999) // iOS only selects "form" elements with SelectionRange
        }

        try {
            document.execCommand("copy") // Security exception may be thrown by some browsers.
        } catch (ex) {
            console.warn("Copy to clipboard failed.", ex)
        }
        toast("Copied")
    }

    function drawHeatmap() {
        const canvas = document.getElementById("player-heatmap")
        if (canvas === null) {
            return
        }

        let chart = new Draw(
            canvas,
            Draw.makeSize(canvas.offsetWidth, canvas.offsetHeight)
        )
        chart.clear()

        let key = "none"
        if (props.selectedHalf === 0) {
            key = "game"
        } else if (props.selectedHalf === 1) {
            key = "first_half"
        } else if (props.selectedHalf === 2) {
            key = "second_half"
        }

        let rowfunc = (row, totalRows) => row
        let colfunc = (col, totalCols) => col

        if (!props.heatmaps || !props.heatmaps[key]) {
            return
        }

        if (props.heatmaps[key].attacking === "left") {
            colfunc = (col, totalCols) => totalCols - col - 1
        }
        if (props.heatmaps[key].attacking === "right") {
            rowfunc = (row, totalRows) => totalRows - row - 1
        }
        if (props.heatmaps[key].attacking === "up") {
            rowfunc = (row, totalRows) => totalRows - row - 1
        }

        const fieldColor = "rgba(0, 0, 0, 0.09)"
        //const fieldLineColor = 'rgba(255, 255, 255, 1)';
        const fieldLineColor = "#707070"

        //// Field
        //// Side line
        const fieldRect = Draw.makeRect(
            9,
            1,
            chart.size.w - 18,
            chart.size.h - 2
        )
        chart.rect(fieldRect, {
            fill: fieldColor,
            stroke: fieldLineColor,
            lineWidth: 2,
        })

        drawHeatGrid(chart, props.heatmaps[key], rowfunc, colfunc)

        //draw field lines on top of the grid
        drawField(chart)
    }

    function drawHeatGrid(chart, heatmap, rowfunc, colfunc) {
        if (heatmap === null) {
            return
        }

        const alpha = function (val) {
            if (val === -1) {
                return 0
            }

            const max = 0.99
            const min = 0.2
            const range = max - min

            const a = range * val + min
            return a
        }

        const grid = heatmap["grid"]
        if (!grid) {
            return
        }
        if (grid.length === 0 || grid[0].length === 0) {
            return
        }
        const cw = (chart.size.w - 20) / heatmap.grid.length
        const rh = (chart.size.h - 4) / heatmap.grid[0].length

        const totalRows = grid.length
        for (let row = 0; row < totalRows; row++) {
            const totalCols = grid[row].length
            for (let col = 0; col < totalCols; col++) {
                const val = grid[row][col]
                const a = alpha(val)
                const fill = "rgba(76,144,224," + a + ")"
                const cellRect = Draw.makeRect(
                    colfunc(col, totalCols) * cw + 10,
                    rowfunc(row, totalRows) * rh + 2,
                    cw,
                    rh
                )
                chart.rect(cellRect, { fill: fill })
            }
        }
    }

    function drawField(chart) {
        //// General Declarations
        //// Color Declarations
        //const fieldLineColor = 'rgba(255, 255, 255, 1)';
        const fieldLineColor = "#f9f9f9"

        //// Field
        //// Side line
        const fieldRect = Draw.makeRect(
            10,
            2,
            chart.size.w - 20,
            chart.size.h - 4
        )
        chart.rect(fieldRect, {
            stroke: fieldLineColor,
            lineWidth: 3,
        })

        //// Mid field line
        const rightPt = Draw.makePoint(10, chart.size.h / 2)
        const leftPt = Draw.makePoint(chart.size.w - 10, chart.size.h / 2)
        chart.line(rightPt, leftPt, {
            stroke: fieldLineColor,
            lineWidth: 3,
        })

        const goalW = chart.size.w / 1.73
        const goalH = chart.size.h / 6
        const centerD = goalW / 2.22

        //// Center Field Oval
        const centerCircleRect = Draw.makeRect(
            (chart.size.w - centerD) / 2,
            (chart.size.h - centerD) / 2,
            centerD,
            centerD
        )
        chart.oval(centerCircleRect, {
            stroke: fieldLineColor,
            lineWidth: 2,
        })

        //// Left Goal Box
        const topGoalRect = Draw.makeRect(
            (chart.size.w - goalW) / 2,
            3,
            goalW,
            goalH
        )
        chart.rect(topGoalRect, {
            stroke: fieldLineColor,
            lineWidth: 3,
        })

        //// Rectangle 3 Drawing
        const bottomGoalRect = Draw.makeRect(
            (chart.size.w - goalW) / 2,
            chart.size.h - 3 - goalH,
            goalW,
            goalH
        )
        chart.rect(bottomGoalRect, {
            stroke: fieldLineColor,
            lineWidth: 3,
        })
    }

    if (props.loading) {
        return renderLoading()
    } else {
        return renderPanel()
    }
}

const mapStateToProps = (state, props) => {
    // Settings
    let selectedHalf = state.selectedHalf

    // player stats
    let playerId = props.match.params.trace_number
    let playerStats = []
    let playerStatsBaseball = state.player_stats_baseball.value
    let hkey = "game"
    if (selectedHalf === 2) {
        hkey = "second_half"
    }
    if (selectedHalf === 1) {
        hkey = "first_half"
    }
    if (state.player_stats.complete) {
        playerStats = state.player_stats.value[hkey].filter(
            (ps) => ps.athlete !== undefined
        )
    }
    let stats = {
        effor: 0,
        effor_rank: 20,
        stamina: 0,
        stamina_rank: 20,
        distance: 0,
        distance_rank: 20,
        top_speed: 0,
        top_speed_rank: 20,
        minutes_played: 0,
        minutes_played_rnak: 20,
    }
    let missingStats = true
    let sportType = state.game.value?.sport_type
    if (sportType === "soccer") {
        for (let stat of playerStats) {
            if (stat.trace_number === playerId) {
                stats = stat
                missingStats = false
                break
            }
        }
    } else if (sportType === "baseball") {
        for (let stat of playerStatsBaseball) {
            if (stat.traceNumber === parseInt(playerId)) {
                stats = stat
                missingStats = false
                break
            }
        }
    }
    let isMe = false
    if (
        state.user.value &&
        state.game.value &&
        state.game.value.home_team &&
        state.game.value.home_team.players
    ) {
        for (let player of state.game.value.home_team.players) {
            if (player.user.user_id === state.user.value.user_id) {
                isMe = true
                break
            }
        }
    }

    // Player
    let player = null
    if (state.game.complete) {
        for (let p of state.game.value.home_team.players) {
            if (p.trace_number === playerId) {
                player = p
                break
            }
        }
    }
    if (player === null) {
        player = {
            user: {
                first_name: "--",
                last_name: "--",
            },
            id: playerId,
            team_player_id: -1,
        }
    }
    let goalEvents = state.game.value?.playlists?.find(
        (e) => e.id === "home-goals"
    )
    let goalCount = goalEvents ? goalEvents.event_count : 0
    // Find the players playlist
    var teamName = props.match.params.team_name
    var gameId = props.match.params.game_id
    var playerPlaylist = null
    var playlistEventId = null
    if (state.game.complete) {
        for (var i = 0; i < state.game.value.playlists.length; i++) {
            var ply = state.game.value.playlists[i]
            var playerScope = ply.scope["player"]
            if (playerScope) {
                var playerIdScope = playerScope["id"]
                if (playerIdScope === playerId) {
                    playerPlaylist = ply
                    break
                }
            }
        }
    }
    if (playerPlaylist) {
        var events = playlistStore.filterEventsWithPlaylist(
            playerPlaylist,
            state.game.value.events,
            playlistStore.buildFilters({ selectedHalf })
        )
        if (events.length > 0) {
            playlistEventId = events[0].event_number
        }
    }

    let loading =
        sportType === "soccer"
            ? !state.game.complete || !state.player_stats.complete
            : !state.game.complete || state.player_stats_baseball.loading

    let user = state.user.value
    let team = state.team.value
    let canEdit = false
    if (user && team) {
        canEdit = team.access_password && team.access_password.length > 0
    }

    let showStats = state.acl.value?.show_stats
    let isPanoramicSensorless = state.game?.value?.video_render_type === "panoramic_sensorless_global_identity"

    let data = {
        user: state.user.value,
        team: state.team.value,
        canEdit: canEdit,
        loading: loading,
        teamName: teamName,
        gameId: gameId,
        traceNumber: playerId,
        playlistId: props.match.params.playlist_id,
        eventNumber: props.match.params.event_number,
        selectedHalf: selectedHalf,
        player: player,
        stats: stats,
        heatmaps: state.heatmap.complete ? state.heatmap.value.map : [],
        playerPlaylist: playerPlaylist,
        playlistEventId: playlistEventId,
        playerStats: playerStats,
        missingStats: missingStats,
        isMe: isMe,
        rev: state.rev,
        game: state.game.value,
        acl: state.acl.value ? state.acl.value : {},
        playerStatsBaseball: playerStatsBaseball,
        sportType: sportType,
        showStats: showStats,
        goalCount: goalCount,
        isPanoramicSensorless: isPanoramicSensorless
    }
    return data
}

const mapDispatchToProps = (dispatch) => {
    return {
        onSelectEvent: (event) => {},
        selectHalf: (selectedHalf) => {
            dispatch({
                type: "SET_FILTER",
                key: "selectedHalf",
                value: selectedHalf,
            })
        },
        load: (teamName, gameId, playerId) => {},
        toast: (message) => {
            dispatch({
                type: "TOAST",
                message: message,
            })
        },
        setBaseballStats: (statsBaseball) => {
            dispatch({
                type: "COMMON_SET",
                key: "player_stats_baseball",
                value: statsBaseball,
            })
        },
    }
}

const PlayerDetailsPanel = connect(
    mapStateToProps,
    mapDispatchToProps
)(PlayerDetailsPanelComp)

export default PlayerDetailsPanel
